import { UserFragment } from "@graphql/users/users.g";
import React from "react";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  DEFAULT_USER_TABLE_COLUMN_ORDER,
  DEFAULT_USER_TABLE_COLUMN_SETTINGS,
  USER_TABLE_COLUMN_LABELS,
  UserTableColumns,
} from "./UI/UsersTableColumns";

/**
 * Default filter values for Users
 */
export const DEFAULT_USER_FILTER_VALUES = {
  nameSearch: "",
  emailSearch: "",
};

/**
 * Comprehensive table configuration for Users
 */
export const UsersTableConfig = createTableConfiguration<
  UserFragment,
  UserTableColumns
>({
  tableId: "users",
  columns: {
    [UserTableColumns.name]: {
      id: UserTableColumns.name,
      label: USER_TABLE_COLUMN_LABELS[UserTableColumns.name],
      fieldPath: "name",
      sortField: "name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: UserFragment) => {
        return renderName(value, row, () => {});
      },
    },
    [UserTableColumns.email]: {
      id: UserTableColumns.email,
      label: USER_TABLE_COLUMN_LABELS[UserTableColumns.email],
      fieldPath: "email",
      sortField: "email",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderEmail(value);
      },
    },
    [UserTableColumns.createdAt]: {
      id: UserTableColumns.createdAt,
      label: USER_TABLE_COLUMN_LABELS[UserTableColumns.createdAt],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCreatedAt(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_USER_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_USER_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = () => ({
  ...DEFAULT_USER_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: {
  nameSearch: string;
  emailSearch: string;
}): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.emailSearch && filterValues.emailSearch.trim()),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderName(
  value: unknown,
  row: UserFragment,
  onDetailsClick: (userId: string) => void,
): React.ReactNode {
  return (
    <Clickable onClick={() => onDetailsClick(row.id)} outline="sm" stretch>
      <Table.UserCell name={String(value)} />
    </Clickable>
  );
}

export function renderEmail(value: unknown): React.ReactNode {
  return <Text truncate>{String(value)}</Text>;
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}
